import { useOrgProductsByIdForScan } from '@kaiizen/kai-pro-api-react-state';
import type { ScanRootOptimized } from '@kaiizen/kai-scan-template-system';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useKaiProApiClient, useScanEditingContextSlice } from '@/Client/State';

import type { ScanAudioAttachScope } from './scanAudioScopeLib';
import { buildVoiceScopeRejectMessage, VOICE_SCOPE_IGNORED_MESSAGE } from './videoToolboxActivityLogLib';
import { attachPhotoToVoiceScopeTasks } from './videoToolboxProposalPhotoLib';
import {
  buildVoiceScopeAgentProvenance,
  buildVoiceScopeScanNotes,
  buildVoiceScopeSnapshot,
  createVoiceScopeCommandRunner,
  processVoiceScopeUtterance,
  type VoiceScopeCommand,
  type VoiceScopeOrchestratorOutcome,
  voiceScopeProducts,
  type VoiceScopeSessionState,
} from './voiceScope';

export type VoiceScopeProposal = {
  proposalId: string;
  proposedAtMs: number;
  utterance: string;
  summary: string;
  commands: VoiceScopeCommand[];
  scanPartId: string;
  confidence: number;
  operationGatePassed: boolean;
  usedBestGuess: boolean;
  jevDetail: string;
};

type QueuedUtterance = {
  utterance: string;
};

type UseVideoToolboxVoiceScopeOptions = {
  open: boolean;
  enabled: boolean;
  attachScope: ScanAudioAttachScope;
  /** User confirmed a proposal; scan commands were applied. */
  onVoiceApplied: (summary: string, utterance: string, confidence: number, jevDetail: string) => void;
  /** Orchestrator returned `propose`; show pending Apply/Dismiss row. */
  onVoiceProposed: (proposal: VoiceScopeProposal) => void;
  onVoiceRejected: (utterance: string, reason: string) => void;
};

/** Queues finalized utterances, calls KaiAgent, and surfaces voice scope edits for review. */
export const useVideoToolboxVoiceScope = ({
  open,
  enabled,
  attachScope,
  onVoiceApplied,
  onVoiceProposed,
  onVoiceRejected,
}: UseVideoToolboxVoiceScopeOptions) => {
  const kaiProApiClient = useKaiProApiClient();
  const { scan, scanDataMutators, scanId, scanIsEditable, scanNoteMutators, scanRoot } = useScanEditingContextSlice([
    'scan',
    'scanDataMutators',
    'scanId',
    'scanIsEditable',
    'scanNoteMutators',
    'scanRoot',
  ]);
  const { orgProductsById } = useOrgProductsByIdForScan(scan);

  const [isThinking, setIsThinking] = useState(false);
  const [pendingProposal, setPendingProposal] = useState<VoiceScopeProposal | undefined>();

  const utteranceQueueRef = useRef<QueuedUtterance[]>([]);
  const processingRef = useRef(false);
  const sessionRef = useRef<VoiceScopeSessionState | undefined>(undefined);
  const recentTaskIdRef = useRef<string | undefined>(undefined);
  const snapshotVersionRef = useRef(0);
  // Mirrors `pendingProposal` synchronously so the queue guards (which run before React
  // re-renders and updates state-derived refs) never drain the queue while a proposal is pending.
  const pendingProposalRef = useRef<VoiceScopeProposal | undefined>(undefined);

  const scanRootRef = useRef<ScanRootOptimized | undefined>(scanRoot);

  useEffect(() => {
    scanRootRef.current = scanRoot;
  }, [scanRoot]);

  const runCommand = useRef(createVoiceScopeCommandRunner(
    scanDataMutators,
    () => scanRootRef.current,
  ));
  runCommand.current = createVoiceScopeCommandRunner(scanDataMutators, () => scanRootRef.current);

  useEffect(() => {
    if (!open) {
      utteranceQueueRef.current = [];
      processingRef.current = false;
      sessionRef.current = undefined;
      recentTaskIdRef.current = undefined;
      snapshotVersionRef.current = 0;
      pendingProposalRef.current = undefined;
      setIsThinking(false);
      setPendingProposal(undefined);
    }
  }, [open]);

  const buildSession = useCallback((): VoiceScopeSessionState | undefined => {
    if (!scanRoot || !attachScope.groupId) return undefined;
    snapshotVersionRef.current += 1;
    return {
      snapshot: buildVoiceScopeSnapshot(scanRoot, attachScope.groupId, {
        version: snapshotVersionRef.current,
        taskId: attachScope.taskId,
        productOf: voiceScopeProducts(orgProductsById),
      }),
      transcript: sessionRef.current?.transcript ?? [],
      recentEdits: sessionRef.current?.recentEdits ?? [],
    };
  }, [attachScope.groupId, attachScope.taskId, orgProductsById, scanRoot]);

  const createVoiceScopeScanNotes = useCallback(async (
    commands: VoiceScopeCommand[],
    utterance: string,
  ) => {
    if (!scanId) throw new Error('Scan ID is required to write voice scope notes');
    await Promise.all(buildVoiceScopeScanNotes(commands, utterance, attachScope).map(note => scanNoteMutators.create(note)));
  }, [attachScope, scanId, scanNoteMutators]);

  const applyCommands = useCallback(async (
    commands: VoiceScopeCommand[],
    utterance: string,
    summary: string,
    proposedAtMs: number,
  ) => {
    if (!scanRootRef.current) throw new Error('Scan root is required to apply voice commands');

    const agentProvenance = buildVoiceScopeAgentProvenance(proposedAtMs);
    for (const command of commands) {
      const updatedScan = await runCommand.current(command, agentProvenance);
      scanRootRef.current = updatedScan;
      recentTaskIdRef.current = command.taskId;
    }

    // Provenance notes are best-effort: the scope edit is already applied, so a note
    // failure must not surface as a rejected edit.
    try {
      await createVoiceScopeScanNotes(commands, utterance);
    } catch (error) {
      console.log('voice scope note write failed', {
        error: error instanceof Error ? error.message : String(error),
        utterance,
      });
    }

    sessionRef.current = buildSession();
    if (sessionRef.current) {
      sessionRef.current.recentEdits = [
        ...(sessionRef.current.recentEdits ?? []),
        summary,
      ].slice(-5);
    }
  }, [buildSession, createVoiceScopeScanNotes]);

  const handleOutcome = useCallback(async (outcome: VoiceScopeOrchestratorOutcome) => {
    switch (outcome.kind) {
      case 'propose': {
        const proposal: VoiceScopeProposal = {
          proposalId: crypto.randomUUID(),
          proposedAtMs: Date.now(),
          utterance: outcome.utterance,
          summary: outcome.summary,
          commands: outcome.commands,
          scanPartId: outcome.scanPartId,
          confidence: outcome.confidence,
          operationGatePassed: outcome.operationGatePassed,
          usedBestGuess: outcome.usedBestGuess,
          jevDetail: outcome.jevDetail,
        };
        pendingProposalRef.current = proposal;
        setPendingProposal(proposal);
        onVoiceProposed(proposal);
        break;
      }
      case 'reject':
        onVoiceRejected(outcome.utterance, buildVoiceScopeRejectMessage(outcome));
        break;
      case 'ignore':
        onVoiceRejected(outcome.utterance, VOICE_SCOPE_IGNORED_MESSAGE);
        break;
    }
  }, [onVoiceProposed, onVoiceRejected]);

  const processQueue = useCallback(async () => {
    if (!enabled || !scanIsEditable || processingRef.current || pendingProposalRef.current) return;

    const queuedUtterance = utteranceQueueRef.current.shift();
    if (!queuedUtterance) return;
    const { utterance } = queuedUtterance;

    const session = buildSession();
    if (!session || !attachScope.groupId) {
      utteranceQueueRef.current.unshift(queuedUtterance);
      return;
    }
    sessionRef.current = session;

    processingRef.current = true;
    setIsThinking(true);
    try {
      const { outcome, transcript } = await processVoiceScopeUtterance(
        utterance,
        session,
        (state, questions) => kaiProApiClient.evaluateJev({ scanId, state, questions }),
        undefined,
        recentTaskIdRef.current,
      );
      sessionRef.current = { ...session, transcript };
      await handleOutcome(outcome);
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'KaiAgent call failed';
      onVoiceRejected(utterance, reason);
    } finally {
      processingRef.current = false;
      setIsThinking(false);
      if (!pendingProposalRef.current) void processQueue();
    }
  }, [
    attachScope.groupId,
    buildSession,
    enabled,
    handleOutcome,
    kaiProApiClient,
    onVoiceRejected,
    scanId,
    scanIsEditable,
  ]);

  const enqueueUtterance = useCallback((utterance: string) => {
    if (!enabled || !scanIsEditable || !attachScope.groupId) return;
    utteranceQueueRef.current.push({ utterance });
    if (!processingRef.current && !pendingProposalRef.current) void processQueue();
  }, [attachScope.groupId, enabled, processQueue, scanIsEditable]);

  const confirmProposal = useCallback(async (selectedPhotoId?: string) => {
    if (!pendingProposal) return;
    const proposal = pendingProposal;
    pendingProposalRef.current = undefined;
    setPendingProposal(undefined);
    try {
      await applyCommands(
        proposal.commands,
        proposal.utterance,
        proposal.summary,
        proposal.proposedAtMs,
      );

      if (selectedPhotoId && scanRootRef.current && attachScope.groupId) {
        const updatedScan = attachPhotoToVoiceScopeTasks(
          scanRootRef.current,
          attachScope.groupId,
          selectedPhotoId,
          proposal.commands,
        );
        if (updatedScan) {
          await scanDataMutators.setScan(updatedScan);
          scanRootRef.current = updatedScan;
        }
      }

      onVoiceApplied(proposal.summary, proposal.utterance, proposal.confidence, proposal.jevDetail);
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Scope edit failed';
      onVoiceRejected(proposal.utterance, reason);
    }
    void processQueue();
  }, [
    applyCommands,
    attachScope.groupId,
    onVoiceApplied,
    onVoiceRejected,
    pendingProposal,
    processQueue,
    scanDataMutators,
  ]);

  const dismissProposal = useCallback(() => {
    if (!pendingProposal) return;
    onVoiceRejected(pendingProposal.utterance, 'Proposal dismissed');
    pendingProposalRef.current = undefined;
    setPendingProposal(undefined);
    void processQueue();
  }, [onVoiceRejected, pendingProposal, processQueue]);

  const micPaused = isThinking || Boolean(pendingProposal);

  return {
    enqueueUtterance,
    isThinking,
    micPaused,
    pendingProposal,
    confirmProposal,
    dismissProposal,
  };
};
